import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import useThrottle from 'src/hooks/useThrottle';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { useStore } from 'src/stores/store';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import { IRecievePayment } from 'src/@types/Choqorak/MainTables/ElectricityPayment/ElectricityPayment';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { GetMonthName } from 'src/sections/common/GetMonthName';
import Label from 'src/components/Label';

// ----------------------------------------------------------------------
interface Props {
  ElectricityPaymentId: number;
  year?: number;
  month?: number;
  searchBy?: string;
}
export default observer(function ELectricityRecievePayment({
  ElectricityPaymentId,
  year,
  month,
  searchBy,
}: Props) {
  const { ElectricityPaymentStore } = useStore();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const { setOpenCloseCreateDialog, RecievePayment, selectedElectricityPaymentDetail } =
    ElectricityPaymentStore;
  const { loadElectricityPayment } = ElectricityPaymentStore;

  const NewRentPaymentSchema = useMemo(
    () =>
      Yup.object().shape({
        paymentDate: Yup.string().required(translate('Validation.paymentDate')),
        paidAmount: Yup.number()
          .typeError(translate('Validation.paidAmount'))
          .required(translate('Validation.paidAmount'))
          .max(
            selectedElectricityPaymentDetail?.paymentAmount ?? Infinity,
            translate('Validation.maxPaidAmount', {
              value: selectedElectricityPaymentDetail?.paymentAmount,
            })
          ),
      }),
    [selectedElectricityPaymentDetail]
  );

  const defaultValues = useMemo<IRecievePayment>(
    () => ({
      electricityPaymentId: selectedElectricityPaymentDetail?.electricityPaymentId,
      paymentDate: new Date(),
      paidAmount: undefined,
      serialNumber: selectedElectricityPaymentDetail?.serialNumber || undefined,
      attachment: selectedElectricityPaymentDetail?.attachment || undefined,
    }),
    [selectedElectricityPaymentDetail]
  );

  const methods = useForm<IRecievePayment>({
    resolver: yupResolver(NewRentPaymentSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = async (data: IRecievePayment) => {
    const newData = {
      ...data,
      paymentDate: new Date(data.paymentDate!),
    };

    try {
      await RecievePayment(newData);

      loadElectricityPayment({
        years: Number(year),
        months: Number(month),
        searchBy: searchBy,
      });

      setOpenCloseCreateDialog();
      reset();
      enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
    } catch (err: any) {
      try {
        const json = JSON.parse(err?.request?.response);

        if (json.error) {
          if (json.error.paidAmount != null) {
            setError('afterSubmit', { type: 'manual', message: json.error.paidAmount });
          } else {
            setError('afterSubmit', { ...err, message: json.message });
          }
        }
      } catch {
        setError('afterSubmit', { type: 'manual', message: err.message || 'Unexpected error' });
      }
    }
  };


  // const yearsDate = watch('year'); // Watching the 'years' field

  // useEffect(() => {
  //   if (yearsDate) {
  //     const changeDateToDateFormat = new Date(yearsDate);

  //     // Extract Persian year and month
  //     const persianYear = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
  //       year: 'numeric',
  //     });
  //     const persianMonth = changeDateToDateFormat.toLocaleString('fa-IR-u-ca-persian', {
  //       month: 'numeric',
  //     });

  //     // Convert Persian numerals to Western numerals
  //     const westernYear = persianYear.replace(/[۰-۹]/g, (digit) =>
  //       '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
  //     );
  //     const westernMonth = persianMonth.replace(/[۰-۹]/g, (digit) =>
  //       '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
  //     );

  //     // Update state with Western year and month
  //     setYear(westernYear);
  //     setMonth(westernMonth);
  //   }
  // }, [yearsDate]);

  const val = watch();
  const paidAmount = watch('paidAmount');
  useEffect(() => {
    setValue('paidAmount', selectedElectricityPaymentDetail?.paymentAmount);
  }, []);
  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* First column for the form */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(1, 1fr)' },
              }}
            >
              <LocalizDatePicker
                name="paymentDate"
                // label={translate('Employee.date')}
                label="paymentDate"
                control={control}
                showAsterisk={true}
              />

              <RHFTextField
                name="paidAmount"
                label={translate('ElectricityPayment.PaidAmount')}
                type="number"
                value={watch('paidAmount') || ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : Number(e.target.value);
                  setValue('paidAmount', value);
                }}
                autoFocus
              />

              <RHFTextField
                name="serialNumber"
                label={translate('RentPaymentPrint.SerialNumber')}
                showAsterisk={true}
              />
              <Stack spacing={2} sx={{ mb: 2 }}>
                <>
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    // accept=".jpg"
                    id="contractattachment"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      setValue('attachment', file);
                    }}
                    style={{ display: 'none' }}
                  />

                  {/* TextField with Attachment Icon */}
                  <TextField
                    label="Attach File"
                    value={watch('attachment')?.name || ''} // Display file name
                    placeholder="No file chosen"
                    InputProps={{
                      readOnly: true, // Prevent manual input
                      startAdornment: (
                        <InputAdornment position="start">
                          {/* Attachment Icon */}
                          <label htmlFor="contractattachment">
                            <IconButton component="span">
                              <AttachFileIcon />
                            </IconButton>
                          </label>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    size="medium"
                    sx={{ m: 1, width: '100%' }}
                  />
                </>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
                disabled={isSubmitting}
                // startIcon={
                //   !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                // }
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                {translate('CRUD.Save')}
              </LoadingButton>
              <Button
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  setOpenCloseCreateDialog();
                }}
              >
                {translate('CRUD.Close')}
              </Button>
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Label color="success">
                <Typography variant="body1">
                  {GetMonthName(selectedElectricityPaymentDetail?.month)}/{' '}
                  {selectedElectricityPaymentDetail?.year}
                </Typography>
              </Label>

              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('Customer.CustomerName')}:</strong>
                    </TableCell>
                    <TableCell>{selectedElectricityPaymentDetail?.customerName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('Shop.ShopCode')}:</strong>
                    </TableCell>
                    <TableCell>{selectedElectricityPaymentDetail?.shopCode}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('contractsInfo.ContractNumber')}:</strong>
                    </TableCell>
                    <TableCell>{selectedElectricityPaymentDetail?.contractNumber}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('ElectricMeter.ElectricBoxCode')}:</strong>
                    </TableCell>
                    <TableCell>{selectedElectricityPaymentDetail?.electricBoxCode}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('ElectricMeter.ElectricMeterSerial')}:</strong>
                    </TableCell>
                    <TableCell>{selectedElectricityPaymentDetail?.electricMeterSerial}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>
                        {translate('ElectricityPayment.PreviousElectricMeterDegree')}:
                      </strong>
                    </TableCell>
                    <TableCell>
                      {selectedElectricityPaymentDetail?.previousElectricMeterDegree}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('ElectricityPayment.CurrentElectricMeterDegree')}:</strong>
                    </TableCell>
                    <TableCell>
                      {selectedElectricityPaymentDetail?.currentElectricMeterDegree}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('ElectricityPayment.TotalUsedKilowats')}:</strong>
                    </TableCell>
                    <TableCell>
                      {Number(selectedElectricityPaymentDetail?.currentElectricMeterDegree) -
                        Number(selectedElectricityPaymentDetail?.previousElectricMeterDegree)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('ElectricityPayment.RemaintsOfThePast')}:</strong>
                    </TableCell>
                    <TableCell>{selectedElectricityPaymentDetail?.remnantsOfThePast}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('RentPayment.PaymentAmount')}:</strong>
                    </TableCell>
                    <TableCell>{selectedElectricityPaymentDetail?.paymentAmount}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('RentPayment.PaidAmount')}:</strong>
                    </TableCell>
                    <TableCell>
                      {selectedElectricityPaymentDetail?.paidAmount === 0
                        ? paidAmount
                        : Number(selectedElectricityPaymentDetail?.paidAmount!) +
                        Number(paidAmount)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>{translate('RentPayment.RemainAmount')}:</strong>
                    </TableCell>
                    <TableCell>
                      {selectedElectricityPaymentDetail?.paidAmount === 0
                        ? Number(selectedElectricityPaymentDetail?.paymentAmount!) -
                        Number(paidAmount)
                        : Number(selectedElectricityPaymentDetail?.paymentAmount!) -
                        (Number(selectedElectricityPaymentDetail?.paidAmount!) +
                          Number(paidAmount))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Card>
        </Grid>

        {/* Second column for displaying year and month information */}
      </Grid>
    </FormProvider>
  );
});
